<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKanban } from 'dashboard/composables/useKanban';
import { useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['create-pipeline', 'edit-pipeline', 'delete-pipeline']);

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { loading, error, fetchPipelines, deletePipeline } = useKanban();

const pipelinesData = ref([]);

const pipelines = computed(() => pipelinesData.value);

const formattedPipelines = computed(() =>
  pipelines.value.map(item => ({
    ...item,
    stagesCount: item.kanban_columns?.length || item.stagesCount || 0,
    conversationsCount: item.conversations_count || item.conversationsCount || 0,
    updatedAt: item.updated_at || item.updatedAt,
  }))
);

const loadPipelines = async () => {
  const data = await fetchPipelines();
  pipelinesData.value = Array.isArray(data) ? data : [];
};

const goToPipeline = pipelineId => {
  router.push(accountScopedRoute('kanban_pipeline_board', { pipelineId }));
};

const handleCreatePipeline = () => {
  emit('create-pipeline');
};

const handleEditPipeline = (pipeline) => {
  emit('edit-pipeline', pipeline);
};

const handleDeletePipeline = (pipeline) => {
  emit('delete-pipeline', pipeline);
};

// Expor loadPipelines para o componente pai
defineExpose({
  loadPipelines
});

onMounted(() => {
  loadPipelines().catch(() => {});
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div class="flex items-center justify-between px-6 py-5 border-b border-n-weak">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('KANBAN.PIPELINES.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('KANBAN.PIPELINES.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          icon="i-lucide-plus"
          :label="t('KANBAN.PIPELINES.CREATE')"
          @click="handleCreatePipeline"
        />
      </div>
    </div>

    <div class="flex-1 p-6 overflow-auto">
      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-slate-9 mx-auto" />
          <p class="text-sm text-n-slate-11 mt-3">
            {{ t('KANBAN.LOADING') }}
          </p>
        </div>
      </div>

      <div v-else-if="error" class="flex justify-center items-center h-64">
        <div class="text-center text-sm text-red-500">
          {{ t('GENERAL.ERROR_DEFAULT') }}
        </div>
      </div>

      <div
        v-else-if="!formattedPipelines.length"
        class="flex flex-col items-center justify-center gap-3 h-64 text-n-slate-10"
      >
        <span class="i-lucide-layers size-10" />
        <p class="text-sm">
          {{ t('KANBAN.PIPELINES.EMPTY_STATE') }}
        </p>
        <Button
          icon="i-lucide-plus"
          :label="t('KANBAN.PIPELINES.CREATE')"
          @click="emit('create-pipeline')"
        />
      </div>

      <div v-else class="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 w-full">
        <div
          v-for="pipeline in formattedPipelines"
          :key="pipeline.id"
          class="border border-n-weak rounded-xl bg-n-solid-1 dark:bg-n-black/40 p-5 flex flex-col gap-4 shadow-sm hover:shadow-md transition-all duration-150"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-base font-medium text-n-slate-12">
                {{ pipeline.name }}
              </h2>
              <p class="text-sm text-n-slate-11 mt-1">
                {{ pipeline.description || t('KANBAN.PIPELINES.NO_DESCRIPTION') }}
              </p>
            </div>
            <div class="relative">
              <button 
                class="text-n-slate-9 hover:text-n-slate-12 p-1 rounded hover:bg-n-solid-2"
                @click="handleDeletePipeline(pipeline)"
                title="Excluir funil"
              >
                <i class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3 text-xs text-n-slate-10">
            <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-2">
              <span class="i-lucide-columns-3 size-4" />
              <span class="text-n-slate-11">
                <strong class="text-n-slate-12">{{ pipeline.stagesCount }}</strong>
                {{ t('KANBAN.PIPELINES.STAGES') }}
              </span>
            </div>
            <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-2">
              <span class="i-lucide-message-circle size-4" />
              <span class="text-n-slate-11">
                <strong class="text-n-slate-12">{{ pipeline.conversationsCount }}</strong>
                {{ t('KANBAN.PIPELINES.CONVERSATIONS') }}
              </span>
            </div>
          </div>

          <div class="flex items-center justify-between text-xs text-n-slate-10">
            <span>
              {{ t('KANBAN.PIPELINES.UPDATED_AT') }}
              <strong class="text-n-slate-11">
                {{ pipeline.updatedAt ? new Date(pipeline.updatedAt).toLocaleDateString() : '-' }}
              </strong>
            </span>
            <div class="flex gap-2">
              <Button
                slate
                xs
                :label="t('KANBAN.PIPELINES.EDIT')"
                @click="handleEditPipeline(pipeline)"
              />
              <Button
                xs
                :label="t('KANBAN.PIPELINES.OPEN')"
                @click="goToPipeline(pipeline.id)"
              />
            </div>
          </div>
        </div>

        <button
          class="border border-dashed border-n-weak rounded-xl p-5 flex flex-col items-center justify-center gap-3 text-n-slate-10 hover:text-n-slate-12 hover:border-n-slate-8 transition"
          @click="handleCreatePipeline"
        >
          <span class="i-lucide-plus-circle size-8" />
          <span class="font-medium text-sm">{{ t('KANBAN.PIPELINES.CREATE_SHORT') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Estilos específicos para o grid de pipelines */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}
</style>



